import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { assessmentApi } from "configs/api";

export interface Question {
  id?: string;
  questionText: string;
  type: "mcq";
  options: {
    text: string;
    isCorrect: boolean;
  }[];
  points: number;
  order: number;
}

export interface Assessment {
  id: string;
  courseID: string;
  moduleID: string;
  title: string;
  description?: string;
  passingScore: number;
  timeLimit?: number;
  questions: Question[];
  createdAt?: string;
  updatedAt?: string;
}

export interface AssessmentAttempt {
  id: string;
  assessmentID: string;
  enrollmentID: string;
  userID: string;
  answers: {
    questionID: string;
    selectedOptions: string[];
  }[];
  score: number;
  passed: boolean;
  completedAt: string;
}

interface AssessmentState {
  assessments: Assessment[];
  currentAssessment: Assessment | null;
  attempts: AssessmentAttempt[];
  loading: boolean;
  error: string | null;
}

const initialState: AssessmentState = {
  assessments: [],
  currentAssessment: null,
  attempts: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchAssessmentsByModule = createAsyncThunk(
  "assessment/fetchByModule",
  async (moduleID: string) => {
    const response = await assessmentApi.get(`/api/assessments/module/${moduleID}`);
    const payload = response?.data ?? response;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  }
);

export const fetchAssessmentsByCourse = createAsyncThunk(
  "assessment/fetchByCourse",
  async (courseID: string) => {
    const response = await assessmentApi.get(`/api/assessments/course/${courseID}`);
    const payload = response?.data ?? response;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  }
);

export const fetchAssessment = createAsyncThunk(
  "assessment/fetchOne",
  async (id: string) => {
    const response = await assessmentApi.get(`/api/assessments/${id}`);
    return response.data?.data || response.data;
  }
);

export const createAssessment = createAsyncThunk(
  "assessment/create",
  async (assessment: Partial<Assessment>) => {
    const response = await assessmentApi.post("/api/assessments", assessment);
    return response.data?.data || response.data;
  }
);

export const updateAssessment = createAsyncThunk(
  "assessment/update",
  async ({ id, data }: { id: string; data: Partial<Assessment> }) => {
    const response = await assessmentApi.put(`/api/assessments/${id}`, data);
    return response.data?.data || response.data;
  }
);

export const deleteAssessment = createAsyncThunk(
  "assessment/delete",
  async (id: string) => {
    await assessmentApi.delete(`/api/assessments/${id}`);
    return id;
  }
);

export const submitAttempt = createAsyncThunk(
  "assessment/submitAttempt",
  async (attemptData: {
    assessmentID: string;
    enrollmentID: string;
    answers: { questionID: string; selectedOptions: string[] }[];
  }) => {
    const response = await assessmentApi.post("/api/assessments/attempts", attemptData);
    return response.data?.data || response.data;
  }
);

export const fetchUserAttempts = createAsyncThunk(
  "assessment/fetchAttempts",
  async ({ assessmentID, enrollmentID }: { assessmentID: string; enrollmentID: string }) => {
    const response = await assessmentApi.get(
      `/api/assessments/attempts/${assessmentID}/${enrollmentID}`
    );
    return response.data?.data || response.data || [];
  }
);

const assessmentSlice = createSlice({
  name: "assessment",
  initialState,
  reducers: {
    setCurrentAssessment: (state, action: PayloadAction<Assessment | null>) => {
      state.currentAssessment = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssessmentsByModule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssessmentsByModule.fulfilled, (state, action) => {
        state.loading = false;
        state.assessments = action.payload;
      })
      .addCase(fetchAssessmentsByModule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch assessments";
      })
      .addCase(fetchAssessmentsByCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssessmentsByCourse.fulfilled, (state, action) => {
        state.loading = false;
        // Merge new assessments, avoiding duplicates by id
        const newAssessments = Array.isArray(action.payload) ? action.payload : [action.payload];
        const existingIds = new Set(state.assessments.map(a => a.id));
        const merged = [...state.assessments];
        for (const a of newAssessments) {
          if (!existingIds.has(a.id)) {
            merged.push(a);
          }
        }
        state.assessments = merged;
      })
      .addCase(fetchAssessmentsByCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch assessments";
      })
      .addCase(fetchAssessment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssessment.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAssessment = action.payload;
      })
      .addCase(fetchAssessment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch assessment";
      })
      .addCase(createAssessment.fulfilled, (state, action) => {
        state.assessments.push(action.payload);
      })
      .addCase(updateAssessment.fulfilled, (state, action) => {
        const index = state.assessments.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.assessments[index] = action.payload;
        }
        if (state.currentAssessment?.id === action.payload.id) {
          state.currentAssessment = action.payload;
        }
      })
      .addCase(deleteAssessment.fulfilled, (state, action) => {
        state.assessments = state.assessments.filter((a) => a.id !== action.payload);
        if (state.currentAssessment?.id === action.payload) {
          state.currentAssessment = null;
        }
      })
      .addCase(submitAttempt.fulfilled, (state, action) => {
        state.attempts.push(action.payload);
      })
      .addCase(fetchUserAttempts.fulfilled, (state, action) => {
        state.attempts = action.payload;
      });
  },
});

export const { setCurrentAssessment, clearError } = assessmentSlice.actions;
export default assessmentSlice.reducer;

